import React, { useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconAddFolder } from 'components/Zui/Icon'
import {
    useAuth,
    useIntl,
    useToggle,
    useContextModal,
    useDebounceSearch,
    useFilter,
    useGetFilterTags,
} from 'hooks'
import {
    removeFilter,
    generatePatientFilterPills,
    generateQuery,
    generateFilter,
    generateSort,
} from 'utils/filter'
import { useGetCollectionsSuggestionsQuery } from 'services'
import {
    setFilter,
    setSort,
    setSearch,
    setFilterParams,
    setCollection,
    clearSuggestion,
    initialFilter,
    createCollection,
} from 'features/collections-list/collectionSlice'
import Create from 'features/collection/Create'
import Searchbar from 'features/searchbar/Searchbar'
import CollectionFilter from 'components/Collection/CollectionFilter'
import CollectionSort from 'components/Collection/CollectionSort'
import FilterPills from 'components/FilterPills'
import { validationSchema } from './validation'

const SearchAndFilter = () => {
    const { t } = useIntl()
    const dispatch = useDispatch()
    const { isAuthorized } = useAuth()
    const { isOpen, onOpen, onClose, onToggle } = useToggle()
    const { filter, sort, filterParams, isFiltered, search } = useSelector(
        state => state.collections
    )
    const { tagOptions } = useGetFilterTags(undefined, { skip: !isAuthorized })

    useEffect(() => {
        return () => {
            closeFilter()
            closeSort()
        }
    }, [])

    const { isTyping, setKeyword, keyword, data, isFetching, isError } = useDebounceSearch(
        useGetCollectionsSuggestionsQuery,
        {
            min: 3,
            query: generateQuery(filterParams.filter(p => ['sort', 'filter'].includes(p.type))),
            skip: !isAuthorized,
        }
    )

    const handleCreateCollection = values => {
        if (!isAuthorized) return
        const payload = {
            name: values.name,
        }
        onToggle()
        dispatch(createCollection(payload))
    }

    const handleApplyFilter = useCallback(values => {
        if (!isAuthorized) return
        const { tag, shared } = values
        const filterData = []

        if (tag && (tag.length > 1 || tag[0] !== 'all')) {
            filterData.push({ tag })
        }
        if (shared) {
            filterData.push({ shared: [shared] })
        }
        dispatch(setFilter(values))
        dispatch(setFilterParams({ type: 'filter', value: generateFilter(filterData) }))
    }, [])

    const handleSort = useCallback(value => {
        if (!isAuthorized) return
        closeSort()
        dispatch(setSort(value))
        dispatch(
            setFilterParams({
                type: 'sort',
                value: generateSort(value, [
                    {
                        key: 'dateDesc',
                        sortBy: 'modifiedDate',
                        orderDesc: true,
                    },
                    {
                        key: 'dateAsc',
                        sortBy: 'modifiedDate',
                        orderDesc: false,
                    },
                    {
                        key: 'az',
                        sortBy: 'name',
                        orderDesc: false,
                    },
                    {
                        key: 'za',
                        sortBy: 'name',
                        orderDesc: true,
                    },
                ]),
            })
        )
    }, [])

    const handleRemoveFilter = useCallback(
        values => {
            let filterPayload = null
            let payload = null

            if (values.key === 'tag') {
                payload = { [values.key]: values.value }
            }
            if (!payload) return

            filterPayload = removeFilter({
                initialFilter,
                filter,
                payload,
            })

            if (!filterPayload) return
            if (!filterPayload.tag.length) {
                filterPayload.tag = ['all']
            }

            handleApplyFilter(filterPayload)
        },
        [filter]
    )

    const {
        FilterModal,
        isOpen: isOpenFilter,
        onClose: closeFilter,
        onToggle: toggleFilter,
    } = useFilter(CollectionFilter, {
        initialValues: filter,
        onSubmit: values => handleApplyFilter(values),
        refId: 'collectionFilter',
        validationSchema: validationSchema(t),
        tags: tagOptions,
    })

    const {
        ContextModal: SortModal,
        isOpen: isOpenSort,
        onClose: closeSort,
        onToggle: toggleSort,
    } = useContextModal(CollectionSort, {
        initialValue: sort,
        onChange: value => handleSort(value),
        refId: 'collectionSort',
        headerText: 'sort.by',
    })

    const handleChange = useCallback(v => {
        setKeyword(v)
    }, [])

    const handleEnter = useCallback(
        v => {
            dispatch(setSearch({ keyword: v, status: true }))
            dispatch(
                setFilterParams({
                    type: 'query',
                    value: [`Query=name=co:"${v}" or tags=co:"${v}"`],
                })
            )
        },
        [dispatch]
    )

    const handleSelectSuggestion = useCallback(
        value => {
            dispatch(setSearch({ keyword: value.name, status: true }))
            if (value.type === 'folder') {
                dispatch(
                    setFilterParams({
                        type: 'query',
                        value: [`CollectionIds=${value.id}`],
                    })
                )
                dispatch(setCollection(value))
            }
            if (value.type === 'tag') {
                dispatch(
                    setFilterParams({
                        type: 'query',
                        value: [`Query=tags=eq:"${value.name}"`],
                    })
                )
            }
        },
        [dispatch]
    )

    const handleClearSuggestion = useCallback(() => {
        dispatch(clearSuggestion())
        dispatch(
            setFilterParams({
                type: 'query',
                value: [],
            })
        )
    }, [dispatch])

    const filterPills = generatePatientFilterPills(filter, {
        tagOptions: tagOptions || [],
    })

    return (
        <>
            <div className="filter-wrap">
                <Searchbar
                    placeholder={t('search.collections')}
                    name="collectionsearch"
                    onEnter={handleEnter}
                    onClear={handleClearSuggestion}
                    onSelectSuggestion={handleSelectSuggestion}
                    data={data}
                    isTyping={(isTyping || isFetching) && !isError}
                    isError
                    onChange={handleChange}
                    disabled={!isAuthorized}
                    value={search}
                />
                <div className={`patinet-sort-filter-wrapper ${keyword.length > 0 && 'hidden'}`}>
                    <div style={{ position: 'relative' }}>
                        <div className="popup-icon-wrapper">
                            <div
                                className={`${isOpenSort ? 'opened' : 'closed'}`}
                                data-ref="collectionSort"
                                onClick={() => isAuthorized && toggleSort()}
                            >
                                <zui-icon-common-sort-sort
                                    slot="icon"
                                    data-ref="collectionSort"
                                ></zui-icon-common-sort-sort>
                            </div>
                        </div>
                        {isAuthorized && <SortModal sort={sort} />}
                    </div>
                    <div style={{ position: 'relative' }}>
                        <div className="popup-icon-wrapper">
                            <div
                                className={`${isOpenFilter ? 'opened' : 'closed'}`}
                                data-ref="collectionFilter"
                                onClick={() => isAuthorized && toggleFilter()}
                            >
                                <zui-icon-common-filter
                                    slot="icon"
                                    data-ref="collectionFilter"
                                ></zui-icon-common-filter>
                            </div>
                        </div>
                        {isAuthorized && <FilterModal />}
                    </div>
                    <div className="popup-icon-wrapper" onClick={() => isAuthorized && onOpen()}>
                        <div className={`${isOpen ? 'opened' : 'closed'}`}>
                            <IconAddFolder size="m" slot="icon"></IconAddFolder>
                        </div>
                    </div>
                    {isOpen && isAuthorized && (
                        <Create onClose={onClose} onSubmit={handleCreateCollection} />
                    )}
                </div>
            </div>
            {isFiltered && <FilterPills data={filterPills} removeFilter={handleRemoveFilter} />}
        </>
    )
}

export default SearchAndFilter
